import Head from 'next/head'
import Link from 'next/link'
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import React from 'react'
import { getGlossaryTerm, wasteGlossaryTerms, GlossaryTerm } from '@src/constants/wasteGlossary'
import classes from '@src/styles/glossary.module.css'

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: wasteGlossaryTerms.map((term) => ({ params: { slug: term.slug } })),
  fallback: false,
})

export const getStaticProps: GetStaticProps<{ term: GlossaryTerm }> = async ({ params }) => {
  const slug = typeof params?.slug === 'string' ? params.slug : ''
  const term = getGlossaryTerm(slug)

  if (!term) return { notFound: true }
  return { props: { term } }
}

function GlossaryTermPage({ term }: InferGetStaticPropsType<typeof getStaticProps>) {
  const relatedTerms = term.related.map((slug) => getGlossaryTerm(slug)).filter(Boolean) as GlossaryTerm[]
  const pageUrl = `https://getdimension.com/glossary/${term.slug}`
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTerm',
    name: term.term,
    termCode: term.slug,
    description: term.definition,
    url: pageUrl,
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: 'Dimension Waste Management Glossary',
      url: 'https://getdimension.com/glossary',
    },
  }
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Dimension', item: 'https://getdimension.com/' },
      { '@type': 'ListItem', position: 2, name: 'Glossary', item: 'https://getdimension.com/glossary' },
      { '@type': 'ListItem', position: 3, name: term.term, item: pageUrl },
    ],
  }

  return (
    <>
      <Head>
        <title>{`${term.term} Definition | Waste Management Glossary | Dimension`}</title>
        <meta name='description' content={`${term.term}: ${term.definition} Learn how the term applies to waste removal and management.`} />
        <link rel='canonical' href={pageUrl} />
        <meta property='og:title' content={`${term.term} Definition | Dimension`} />
        <meta property='og:description' content={term.definition} />
        <meta property='og:url' content={pageUrl} />
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      </Head>
      <main className={classes.page}>
        <div className={classes.inner}>
          <nav className={classes.breadcrumb} aria-label='Breadcrumb'>
            <Link href='/'>Dimension</Link>
            <span aria-hidden='true'>/</span>
            <Link href='/glossary'>Glossary</Link>
            <span aria-hidden='true'>/</span>
            <span>{term.term}</span>
          </nav>

          <div className={classes.detailWrap}>
            <article className={classes.detailCard}>
              <span className={classes.category}>{term.category}</span>
              <h1 className={classes.detailTitle}>{term.term}</h1>
              <p className={classes.definition}>{term.definition}</p>
              <p className={classes.note}>{term.practicalNote}</p>

              {relatedTerms.length > 0 && (
                <section className={classes.related} aria-labelledby='related-terms'>
                  <h2 className={classes.relatedTitle} id='related-terms'>
                    Related terms
                  </h2>
                  <div className={classes.relatedLinks}>
                    {relatedTerms.map((relatedTerm) => (
                      <Link href={`/glossary/${relatedTerm.slug}`} key={relatedTerm.slug}>
                        {relatedTerm.term}
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
            <Link className={classes.backLink} href='/glossary'>
              Back to the full glossary
            </Link>
          </div>
        </div>
      </main>
    </>
  )
}

export default GlossaryTermPage
